import React from "react";
import { Link } from "react-router-dom";
import logo from "../../images/Recurso 7.svg";
import "../../styles/public-entry.css";

// # PUBLIC ENTRY: scoped components, without changing authenticated UI.
export function EntryIcon({ name = "arrow", size = 22, ...props }) {
  const paths = {
    arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
    back: <path d="M19 12H5m6 6-6-6 6-6" />,
    check: <path d="m5 12 4 4L19 6" />,
    web: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="3" />
        <path d="M3 9h18M7 6.5h.01m3 0h.01M7 13h5m-5 3h8" />
      </>
    ),
    bag: (
      <>
        <path d="M5 7h14l1 14H4L5 7Z" />
        <path d="M9 8V6a3 3 0 0 1 6 0v2" />
      </>
    ),
    chart: <path d="M4 3v17h17M8 15v-4m5 4V7m5 8v-6" />,
    people: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 21v-3a6 6 0 0 1 12 0v3m1-16a3 3 0 0 1 0 6m2 4a5 5 0 0 1 3 4v2" />
      </>
    ),
    menu: <path d="M4 6h16M4 12h16M4 18h16" />,
    close: <path d="m6 6 12 12M6 18 18 6" />,
    eye: (
      <>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    eyeOff: (
      <>
        <path d="m3 3 18 18M10.6 5.1 12 5c6.5 0 10 7 10 7a20 20 0 0 1-3.2 3.9M6.2 6.2A20 20 0 0 0 2 12s3.5 7 10 7a12 12 0 0 0 5.8-1.8M10 10a2.8 2.8 0 0 0 4 4" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    lock: (
      <>
        <rect x="5" y="10" width="14" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3m-4 4v3" />
      </>
    ),
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {paths[name] || paths.arrow}
    </svg>
  );
}

export function EntryBrand({ to = "/" }) {
  return (
    <Link className="wp-entry-brand" to={to} aria-label="WebProfit, inicio">
      <img src={logo} alt="" width="39" height="30" />
      <span>
        WebProfit<span className="wp-entry-brand-dot">.</span>
      </span>
    </Link>
  );
}

export function scrollToEntrySection(id) {
  const target = document.getElementById(id);
  if (!target) return;
  target.scrollIntoView({
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth",
    block: "start",
  });
  target.focus({ preventScroll: true });
}

export function PublicHomeNavigation({
  user,
  onContact,
  loginHref = "/login",
}) {
  const [open, setOpen] = React.useState(false);
  const toggleRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const goTo = (id) => {
    setOpen(false);
    scrollToEntrySection(id);
  };

  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onPointerDown = (event) => {
      if (
        !panelRef.current?.contains(event.target) &&
        !toggleRef.current?.contains(event.target)
      )
        setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  const links = (
    <>
      <button onClick={() => goTo("producto")}>Qué podés hacer</button>
      <button onClick={() => goTo("como-funciona")}>Cómo funciona</button>
      <button
        onClick={() => {
          setOpen(false);
          onContact();
        }}
      >
        Hablemos
      </button>
    </>
  );
  return (
    <header className="wp-entry wp-home-header">
      <a
        className="wp-entry-skip"
        href="#contenido-home"
        onClick={(event) => {
          event.preventDefault();
          goTo("contenido-home");
        }}
      >
        Ir al contenido
      </a>
      <div className="wp-entry-container wp-home-nav">
        <EntryBrand />
        <nav className="wp-home-nav-links" aria-label="Navegación principal">
          {links}
        </nav>
        <div className="wp-home-nav-account">
          <Link className="wp-entry-login" to={user ? "/dashboard" : loginHref}>
            {user ? "Mi panel" : "Ingresar"}
            <EntryIcon size={17} />
          </Link>
          <button
            ref={toggleRef}
            className="wp-entry-menu"
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
            aria-controls="wp-home-mobile-nav"
            onClick={() => setOpen((value) => !value)}
          >
            <EntryIcon name={open ? "close" : "menu"} />
          </button>
        </div>
      </div>
      {open && (
        <nav
          ref={panelRef}
          id="wp-home-mobile-nav"
          className="wp-home-mobile-nav"
          aria-label="Navegación móvil"
        >
          {links}
        </nav>
      )}
    </header>
  );
}

export function PublicHomeFooter({ onContact }) {
  return (
    <footer className="wp-entry wp-home-footer">
      <div className="wp-entry-container">
        <div className="wp-home-footer-top">
          <EntryBrand />
          <p>
            Tu web, tus ventas y tu gestión.
            <br />
            Conectadas, como tiene que ser.
          </p>
          <nav aria-label="Recursos">
            <Link to="/curso">Curso y recursos</Link>
            <button onClick={onContact}>Contacto y soporte</button>
          </nav>
        </div>
        <div className="wp-home-footer-bottom">
          <span>Hecho para acompañar a tu negocio.</span>
          <span>© {new Date().getFullYear()} WebProfit</span>
        </div>
      </div>
    </footer>
  );
}
